import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export default function AdminDashboard() {
  const { profile } = useAuth();
  const [stats, setStats] = useState(null);
  const [myCoaches, setMyCoaches] = useState([]);
  const [recentTxns, setRecentTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (profile?.id) fetchStats(); }, [profile]);

  const fetchStats = async () => {
    try {
      const now = new Date();
      const mtdStart = new Date(now.getFullYear(), now.getMonth(), 1);

      // Get coaches I mentor
      const { data: menteeProfiles } = await supabase
        .from('coach_profiles')
        .select('user_id, hma_percentage, mentor_percentage')
        .eq('mentor_id', profile.id);

      const menteeIds = (menteeProfiles || []).map(m => m.user_id);

      // MTD transactions across all coaches (for total HMA view)
      const { data: allMtdTxns } = await supabase
        .from('transactions')
        .select('gross_amount, hma_amount, hma_company_amount, mentor_amount, coach_id, created_at')
        .eq('status', 'completed')
        .gte('created_at', mtdStart.toISOString());

      const allTxns = allMtdTxns || [];

      // My mentor earnings MTD (from coaches I mentor)
      const myMentorTxns = menteeIds.length > 0
        ? allTxns.filter(t => menteeIds.includes(t.coach_id))
        : [];
      const myMentorEarnings = myMentorTxns.reduce((s, t) => s + Number(t.mentor_amount || 0), 0);

      // Total HMA revenue MTD
      const totalHmaRevenue = allTxns.reduce((s, t) => s + Number(t.hma_company_amount || 0), 0);
      const totalGross = allTxns.reduce((s, t) => s + Number(t.gross_amount || 0), 0);

      // Coach counts
      const { count: totalCoaches } = await supabase
        .from('profiles').select('id', { count: 'exact' })
        .in('role', ['coach', 'senior_coach']);

      const { count: totalClients } = await supabase
        .from('clients').select('id', { count: 'exact' });

      // Per-coach breakdown for coaches I mentor
      const coachBreakdown = [];
      for (const mentee of menteeProfiles || []) {
        const { data: coachProfile } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', mentee.user_id)
          .single();

        const coachTxns = allTxns.filter(t => t.coach_id === mentee.user_id);
        const coachGross = coachTxns.reduce((s, t) => s + Number(t.gross_amount || 0), 0);
        const myEarnings = coachTxns.reduce((s, t) => s + Number(t.mentor_amount || 0), 0);

        coachBreakdown.push({
          id: mentee.user_id,
          name: coachProfile?.full_name || coachProfile?.email || 'Unknown',
          email: coachProfile?.email,
          gross: coachGross,
          myEarnings,
          mentorPct: mentee.mentor_percentage,
          payments: coachTxns.length
        });
      }

      // Sort by gross descending
      coachBreakdown.sort((a, b) => b.gross - a.gross);

      // Recent transactions (all)
      const { data: recent } = await supabase
        .from('transactions')
        .select('*, coach:coach_id(full_name)')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(5);

      setStats({
        totalCoaches,
        totalClients,
        myMentorEarnings,
        totalHmaRevenue,
        totalGross,
        menteeCount: menteeIds.length,
        mtdPayments: allTxns.length
      });
      setMyCoaches(coachBreakdown);
      setRecentTxns(recent || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2 })}`;
  const monthName = new Date().toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });

  if (loading) return <div style={{ color: 'var(--text-secondary)' }}>Loading...</div>;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>HMA Overview</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          Month-to-date — <strong style={{ color: 'var(--text-primary)' }}>{monthName}</strong>
        </p>
      </div>

      {/* MTD Stats */}
      <div className="stat-grid" style={{ marginBottom: 28 }}>
        {[
          { label: 'HMA Revenue (MTD)', value: fmt(stats?.totalHmaRevenue), sub: 'company portion', icon: '◆', highlight: true },
          { label: 'My Mentor Earnings', value: fmt(stats?.myMentorEarnings), sub: `from ${stats?.menteeCount} coaches`, icon: '✦' },
          { label: 'Total Gross (MTD)', value: fmt(stats?.totalGross), sub: 'all coaches combined', icon: '◇' },
          { label: 'Total Coaches', value: stats?.totalCoaches, sub: 'active on platform', icon: '◉' },
          { label: 'Total Clients', value: stats?.totalClients, sub: 'across all coaches', icon: '◎' },
          { label: 'Payments (MTD)', value: stats?.mtdPayments, sub: 'transactions processed', icon: '◈' }
        ].map((s, i) => (
          <div key={i} className="stat-card" style={s.highlight ? { borderColor: 'rgba(201,169,110,0.4)', background: 'rgba(201,169,110,0.05)' } : {}}>
            <div className="stat-label">{s.icon} {s.label}</div>
            <div className="stat-value" style={s.highlight ? { color: 'var(--accent)' } : {}}>{s.value ?? '—'}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* My Coaches This Month */}
      {myCoaches.length > 0 && (
        <div className="card" style={{ marginBottom: 28, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ fontSize: 18 }}>Coaches I Mentor — {monthName}</h3>
            <Link to="/partner/coaches" className="btn btn-secondary btn-sm">Manage All</Link>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Coach</th>
                <th>Their Gross (MTD)</th>
                <th>My Earnings (MTD)</th>
                <th>My %</th>
                <th>Payments</th>
              </tr>
            </thead>
            <tbody>
              {myCoaches.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.email}</div>
                  </td>
                  <td style={{ fontWeight: 500 }}>{fmt(c.gross)}</td>
                  <td style={{ color: 'var(--success)', fontWeight: 600 }}>{fmt(c.myEarnings)}</td>
                  <td style={{ color: 'var(--accent)' }}>{c.mentorPct}%</td>
                  <td style={{ color: 'var(--text-muted)' }}>{c.payments}</td>
                </tr>
              ))}
              {/* Totals row */}
              <tr style={{ background: 'var(--bg-secondary)' }}>
                <td style={{ fontWeight: 600 }}>Total</td>
                <td style={{ fontWeight: 600 }}>{fmt(myCoaches.reduce((s, c) => s + c.gross, 0))}</td>
                <td style={{ color: 'var(--success)', fontWeight: 700 }}>{fmt(myCoaches.reduce((s, c) => s + c.myEarnings, 0))}</td>
                <td>—</td>
                <td style={{ fontWeight: 500 }}>{myCoaches.reduce((s, c) => s + c.payments, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {myCoaches.length === 0 && (
        <div className="card" style={{ marginBottom: 28, textAlign: 'center', padding: '40px 24px', color: 'var(--text-muted)' }}>
          You don't have any coaches assigned to you as mentor yet.
          <br />
          <Link to="/partner/coaches" style={{ color: 'var(--accent)', marginTop: 8, display: 'inline-block' }}>
            Assign yourself as mentor to coaches →
          </Link>
        </div>
      )}

      {/* Recent transactions */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18 }}>Recent Transactions</h3>
          <Link to="/partner/transactions" className="btn btn-secondary btn-sm">View All</Link>
        </div>
        {recentTxns.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 14, textAlign: 'center', padding: '20px 0' }}>
            No transactions yet.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Coach</th><th>Description</th><th>Gross</th><th>HMA Cut</th><th>Date</th></tr>
            </thead>
            <tbody>
              {recentTxns.map(t => (
                <tr key={t.id}>
                  <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t.coach?.full_name || '—'}</td>
                  <td>{t.description || 'Payment'}</td>
                  <td>{fmt(t.gross_amount)}</td>
                  <td style={{ color: 'var(--accent)' }}>{fmt(t.hma_amount)}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{new Date(t.created_at).toLocaleDateString('en-CA')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
